import z from 'zod';
import {
  ApiClient,
  ApiClientContext,
  ApiClientOptions,
  Page,
  PaginatedRouteOptions,
  PaginationQueryParams,
  RouteOptions,
  defaultAxiosConfig,
} from '@qualifio/api-client';
import { AxiosInstance } from 'axios';
import { ProfileAttributes } from '../profile-api-client';
import {
  PromocodeAttributes,
  PromocodeImportAttributes,
  PromocodeStatsAttributes,
} from './promocodes.types';
import { RewardAttributes, RewardsSettings } from './rewards.types';

export type GetRewardQueryParams = { withDeleted?: boolean };
export type ObtainPromocodeBody = {
  profile: ProfileAttributes;
  profileRewardId: string;
};

export const $DeleteUnwonPromocodesRouteQuery = z.object({
  promocodeImportId: z.string().optional(),
});

export const $rewardTypeZod = z.enum(['PROMOCODE', 'CAMPAIGN_ACCESS', 'CAMPAIGN_CODE']);

export const $getRewardsQueryParams = z.object({
  types: z.array($rewardTypeZod).optional().nullish(),
  ids: z.array(z.string()).optional().nullish(),
});

export type GetRewardsQueryParams = z.infer<typeof $getRewardsQueryParams>;

export type DeleteUnwonPromocodesRouteQuery = z.infer<typeof $DeleteUnwonPromocodesRouteQuery>;

export type AddRewardBody = Pick<RewardAttributes, 'i18nAttributes' | 'type'>;

export type UpdateRewardBody = Pick<RewardAttributes, 'i18nAttributes' | 'data' | 'clientIds'>; //clientIds is deprecated

export interface ObtainPromocodeResponse {
  promocode: PromocodeAttributes;
  promocodeStats: PromocodeStatsAttributes;
}

export interface GetPromocodesQueryParams extends PaginationQueryParams {
  search?: string;
  profileIds?: string[];
}

export class RewardsApiClient extends ApiClient {
  static serviceName = 'rewards-api';

  constructor(http?: AxiosInstance, context?: ApiClientContext, options?: ApiClientOptions) {
    super('/api/rewards', http, context, options);
  }

  async addReward(
    programId: string,
    attributes: AddRewardBody,
    options?: RouteOptions,
  ): Promise<RewardAttributes> {
    const { data } = await this.http.post(
      `/programs/${programId}/rewards`,
      attributes,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async archiveReward(id: string, options?: RouteOptions): Promise<RewardAttributes> {
    const { data } = await this.http.delete(`/rewards/${id}`, defaultAxiosConfig(options));
    return data;
  }

  async deletePromocode(
    rewardId: string,
    promocodeId: string,
    options?: RouteOptions,
  ): Promise<void> {
    await this.http.delete(
      `/rewards/${rewardId}/promocodes/${promocodeId}`,
      defaultAxiosConfig(options),
    );
  }

  async deleteUnwonPromocodes(
    rewardId: string,
    options?: RouteOptions<DeleteUnwonPromocodesRouteQuery>,
  ): Promise<PromocodeStatsAttributes> {
    const { data } = await this.http.delete(
      `/rewards/${rewardId}/promocodes/unwon`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getPromocodes(
    rewardId: string,
    options?: RouteOptions<GetPromocodesQueryParams>,
  ): Promise<Page<PromocodeAttributes>> {
    const { data } = await this.http.get(
      `/rewards/${rewardId}/promocodes`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getPromocodeImports(
    rewardId: string,
    options: PaginatedRouteOptions = {},
  ): Promise<Page<PromocodeImportAttributes>> {
    const { data } = await this.http.get<Page<PromocodeImportAttributes>>(
      `/rewards/${rewardId}/promocode-imports`,
      {
        ...defaultAxiosConfig(options),
        params: {
          ...options?.params,
          ...options?.pagination,
        },
      },
    );
    return data;
  }

  async getPromocodeImport(id: string, options?: RouteOptions): Promise<PromocodeImportAttributes> {
    const { data } = await this.http.get<PromocodeImportAttributes>(
      `/promocode-imports/${id}`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getRewards(
    programId: string,
    options?: RouteOptions<GetRewardsQueryParams>,
  ): Promise<RewardAttributes[]> {
    const { data } = await this.http.get(
      `/programs/${programId}/rewards`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getAccountRewards(
    accountId: string,
    options?: RouteOptions<GetRewardsQueryParams>,
  ): Promise<RewardAttributes[]> {
    const { data } = await this.http.get(
      `/accounts/${accountId}/rewards`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getReward(
    id: string,
    options?: RouteOptions<GetRewardQueryParams>,
  ): Promise<RewardAttributes> {
    const { data } = await this.http.get(`/rewards/${id}`, defaultAxiosConfig(options));
    return data;
  }

  async obtainPromocode(
    rewardId: string,
    profile: ProfileAttributes,
    profileRewardId: string,
    options?: RouteOptions,
  ): Promise<ObtainPromocodeResponse> {
    const { data } = await this.http.post(
      `/rewards/${rewardId}/promocodes/obtain`,
      <ObtainPromocodeBody>{ profile, profileRewardId },
      defaultAxiosConfig(options),
    );
    return data;
  }

  async restoreReward(id: string, options?: RouteOptions): Promise<RewardAttributes> {
    const { data } = await this.http.patch(
      `/rewards/${id}/restore`,
      {},
      defaultAxiosConfig(options),
    );
    return data;
  }

  async updateReward(
    id: string,
    attributes: UpdateRewardBody,
    options?: RouteOptions,
  ): Promise<RewardAttributes> {
    const { data } = await this.http.patch(
      `/rewards/${id}`,
      attributes,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async uploadPromocodes(
    rewardId: string,
    rawFile: File,
    options?: RouteOptions,
  ): Promise<PromocodeImportAttributes> {
    const file = new File([rawFile], rawFile.name, {
      type: 'text/csv',
    });
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await this.http.put(`/rewards/${rewardId}/promocodes`, formData, {
      ...defaultAxiosConfig(options),
      headers: { 'content-type': 'multipart/form-data' },
    });

    return data;
  }

  async resetProgram(programId: string, options?: RouteOptions): Promise<void> {
    await this.http.delete(`/programs/${programId}/reset`, defaultAxiosConfig(options));
  }

  async getSettings(options?: RouteOptions): Promise<RewardsSettings> {
    const { data } = await this.http.get('/settings', defaultAxiosConfig(options));
    return data;
  }
}
